import { calculerTarif, type PrixUnitaires, type Tarif } from './tarification'
import type { Organisation, PalierCode, Plan, StatutAbonnement } from './types'

/**
 * Règles de palier et de MRR — logique pure, testée.
 *
 * ⚠️ La version HTML calculait le MRR avec un barème CODÉ EN DUR
 * (`PRIX={starter:15000,pro:35000,business:0}`, grille de l'époque) : changer un prix dans la table
 * `plans` ne changeait rien à l'écran. Ici les prix viennent de `plans`, et un
 * palier sans prix (Business, sur devis) n'est jamais compté à 0 en silence :
 * il est dénombré à part.
 *
 * Depuis la 0027, le MRR d'une organisation suit la règle UNIQUE de
 * `tarification.ts` : palier + véhicules supplémentaires + boîtiers GPS actifs,
 * aux prix figés à sa souscription ; un abonnement annuel est ramené au mois
 * (total annuel / 12).
 */

/** Un essai est échu quand sa date de fin est passée. Même règle que `org_tier()` (0018). */
export function essaiEchu(
  statut: StatutAbonnement | null,
  finPeriode: string | null,
  maintenant: Date,
): boolean {
  if (statut !== 'trialing' || !finPeriode) return false
  const t = Date.parse(finPeriode)
  return Number.isFinite(t) && t <= maintenant.getTime()
}

/**
 * Le palier réellement appliqué par la base — miroir de `public.org_tier()` :
 * opératrice → business ; essai non échu, actif, impayé → son palier ;
 * sinon (résilié, essai échu, sans abonnement) → starter.
 * `active` n'expire PAS sur sa date de fin (aucun renouvellement automatique).
 */
export function palierEffectif(
  e: {
    estOperatrice: boolean
    palier: PalierCode
    statut: StatutAbonnement | null
    finPeriode: string | null
  },
  maintenant: Date,
): PalierCode {
  if (e.estOperatrice) return 'business'
  if (e.statut === 'active' || e.statut === 'past_due') return e.palier
  if (e.statut === 'trialing' && !essaiEchu(e.statut, e.finPeriode, maintenant)) return e.palier
  return 'starter'
}

export function palierEffectifOrg(o: Organisation, maintenant: Date): PalierCode {
  return palierEffectif(
    { estOperatrice: o.is_operator, palier: o.plan_tier, statut: o.sub_status, finPeriode: o.period_end },
    maintenant,
  )
}

export interface ResumeMrr {
  /** Abonnements `active` + `past_due` : palier + options, annuel ramené au mois. */
  mrr: number
  /** Part du MRR portée par des abonnements impayés (dans `mrr`, pas en plus). */
  mrrImpaye: number
  nbPayants: number
  nbImpayes: number
  /** Valeur mensuelle POTENTIELLE des essais en cours — jamais ajoutée au MRR. */
  potentielEssais: number
  nbEssais: number
  /** Essais échus : ils ne comptent ni dans le MRR ni dans le potentiel. */
  nbEssaisEchus: number
  /** Abonnements payants dont le palier n'a pas de prix public (sur devis). */
  sansPrix: number
  /** Abonnements payants facturés à l'année (dans `nbPayants`). */
  nbAnnuels: number
  /** Part du MRR portée par les options (véhicules supplémentaires, boîtiers GPS), annuel ramené au mois. */
  mrrOptions: number
}

export function prixDuPalier(plans: readonly Plan[], palier: PalierCode): number | null {
  const p = plans.find((x) => x.tier === palier)
  return p && typeof p.price_fcfa === 'number' ? p.price_fcfa : null
}

/** Prix unitaires du CATALOGUE (table `plans`) pour un palier ; null si le palier est absent. */
export function prixCatalogue(plans: readonly Plan[], palier: PalierCode): PrixUnitaires | null {
  const p = plans.find((x) => x.tier === palier)
  if (!p) return null
  return {
    base: p.price_fcfa,
    vehiculeSupp: p.extra_vehicle_price_fcfa,
    boitier: p.gps_device_price_fcfa,
    moisPayesAnnuel: p.annual_months_paid,
  }
}

/**
 * Le tarif d'une organisation : ses prix appliqués (figés, lus par
 * `platform_tarifs()`) ou, à défaut, le catalogue ; ses options ; sa période.
 * null = prix du palier inconnu (palier sans plan, ou sur devis).
 */
export function tarifOrganisation(o: Organisation, plans: readonly Plan[]): Tarif | null {
  const prix = o.prix_appliques ?? prixCatalogue(plans, o.plan_tier)
  if (!prix || prix.base === null) return null
  return calculerTarif(prix, {
    // Un palier sans prix unitaire (Business) ne porte pas de supplémentaires : on n'en invente pas.
    vehiculesSupp: prix.vehiculeSupp === null ? 0 : o.extra_vehicles,
    boitiersActifs: o.gps_devices,
    periode: o.billing_period,
  })
}

export function calculerMrr(
  organisations: readonly Organisation[],
  plans: readonly Plan[],
  maintenant: Date,
): ResumeMrr {
  const r: ResumeMrr = {
    mrr: 0, mrrImpaye: 0, nbPayants: 0, nbImpayes: 0,
    potentielEssais: 0, nbEssais: 0, nbEssaisEchus: 0, sansPrix: 0,
    nbAnnuels: 0, mrrOptions: 0,
  }
  for (const o of organisations) {
    if (o.is_operator) continue // « c'est nous » : aucun plan, aucun revenu d'abonnement
    const tarif = tarifOrganisation(o, plans)
    if (o.sub_status === 'active' || o.sub_status === 'past_due') {
      r.nbPayants += 1
      if (o.sub_status === 'past_due') r.nbImpayes += 1
      if (o.billing_period === 'annual') r.nbAnnuels += 1
      if (tarif === null || tarif.mrr === null) {
        r.sansPrix += 1
      } else {
        r.mrr += tarif.mrr
        if (o.sub_status === 'past_due') r.mrrImpaye += tarif.mrr
        r.mrrOptions += mrrDesOptions(tarif, o.billing_period)
      }
    } else if (o.sub_status === 'trialing') {
      if (essaiEchu(o.sub_status, o.period_end, maintenant)) {
        r.nbEssaisEchus += 1
      } else {
        r.nbEssais += 1
        if (tarif !== null && tarif.mrr !== null) r.potentielEssais += tarif.mrr
      }
    }
  }
  return r
}

/** Part « options » du MRR : mensuel des options, ramené au mois comme le total (annuel × mois payés / 12). */
function mrrDesOptions(t: Tarif, periode: Organisation['billing_period']): number {
  const options = t.montantVehiculesSupp + t.montantBoitiers
  return periode === 'annual' ? Math.round((options * t.moisFactures) / 12) : options
}

/**
 * Écarts entre le MRR de la base (`platform_tarifs().mrr_fcfa`) et celui que la
 * console recalcule avec la même règle. Aucun écart attendu : la liste n'est
 * non vide que si la règle SQL et la règle TypeScript ont divergé.
 */
export function ecartsDeMrr(organisations: readonly Organisation[], plans: readonly Plan[]): string[] {
  const noms: string[] = []
  for (const o of organisations) {
    if (o.is_operator || o.mrr_base === null) continue
    const t = tarifOrganisation(o, plans)
    if (t === null || t.mrr === null || t.mrr !== o.mrr_base) noms.push(o.brand_name || o.name)
  }
  return noms
}

/** Répartition des tenants par palier EFFECTIF (pas le palier facturé, qui ment après un essai échu). */
export function repartitionPaliers(
  organisations: readonly Organisation[],
  maintenant: Date,
): Record<PalierCode, number> {
  const t: Record<PalierCode, number> = { starter: 0, pro: 0, business: 0 }
  for (const o of organisations) {
    if (o.is_operator) continue
    t[palierEffectifOrg(o, maintenant)] += 1
  }
  return t
}
